import { keys as trackingKeys } from "@gromia/tracking";
import { fromPairs } from "@gromia/utils/fp";
import * as A from "fp-ts/lib/Array";
import { pipe } from "fp-ts/lib/function";
import * as O from "fp-ts/lib/Option";
import * as R from "fp-ts/lib/Record";
import { navigate } from "gatsby";
import * as t from "io-ts";
import queryString from "query-string";
import * as React from "react";

type Params = Record<string, string | (string | null)[] | null | undefined>;

export function useParams() {
  function getParams(): Params {
    const ssr = typeof window === "undefined";
    return ssr
      ? {}
      : queryString.parse(window.location.search, {
        arrayFormat: "bracket",
      });
  }

  function search(newParams: Params): string {
    const params = getParams();
    const search: string = queryString.stringify(
      Object.assign({}, params, newParams),
      {
        arrayFormat: "bracket",
      },
    );
    return `?${search}`;
  }

  function setParams(newParams: Params) {
    navigate(search(newParams));
  }

  function parseParamO(p: Params[string]): O.Option<string> {
    return pipe(t.string.decode(p), O.fromEither);
  }

  function parseParam<T>(p: Params[string], onErr: T): string | T {
    const x: O.Option<string> = pipe(t.string.decode(p), O.fromEither);

    if (O.isSome(x)) {
      return x.value;
    }

    return onErr;
  }

  function parseT<T extends t.Any, I extends t.TypeOf<T>>(
    decoder: T,
    onErr: () => I,
    p: Params[string],
  ): I {
    const x: O.Option<I> = pipe(decoder.decode(p), O.fromEither);

    if (O.isSome(x)) {
      return x.value;
    }

    return onErr();
  }

  function parseParamsToRecord(keys: string[]) {
    const p = getParams();

    const values = pipe(
      keys,
      A.map(x => pipe(t.string.decode(p[x]), O.fromEither)),
    );

    return pipe(A.zip(keys, values), fromPairs, R.compact);
  }

  function linkWithTracking(uri: string): string {
    const res = queryString.parseUrl(uri);
    const record = parseParamsToRecord([...trackingKeys, "ref", "agent"]);
    const url = res.url;
    const params = { ...res.query, ...record };
    return R.size(params) > 0
      ? [url, queryString.stringify(params)].join("?")
      : url;
  }

  return {
    params: getParams(),
    search,
    setParams,
    parse: parseParam,
    parseO: parseParamO,
    parseT,
    linkWithTracking,
  };
}

export function useSearchParamsState<
  A extends t.Props,
  T extends t.InterfaceType<A>,
  I extends t.TypeOf<T>,
>(decoder: T, defaultValue: I) {
  const p = useParams();
  const [, setState] = React.useState(p.search({}));
  const partial = t.partial(decoder.props);

  const params: I = pipe(
    partial.decode(p.params),
    O.fromEither,
    O.map(x => Object.assign({}, x, defaultValue, x)),
    O.getOrElse(() => defaultValue),
  );

  const set = <X extends keyof A>(k: X) => (v: I[X]) => {
    const e = decoder.props[k];
    const obj = { [k]: e.encode(v) };
    p.setParams(obj);
    setState(p.search(obj));
  };

  return { params, set };
}
